import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '../components/common';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Typography from '../components/common/Typography';
import StatusBadge from '../components/common/StatusBadge';
import { getPrivateSessions, updateSessionStatus } from '../services/privateSessionService';
import { deleteSession } from '../services/sessionManagementService';
import CreatePrivateSessionModal from '../components/private/CreatePrivateSessionModal';
import JoinSessionModal from '../components/private/JoinSessionModal';
import AddPlayerModal from '../components/private/AddPlayerModal';
import CancelDeleteModal from '../components/sessions/CancelDeleteModal';

const PrivateSessionsPage = ({ currentUser }) => {
  const navigate = useNavigate();
  
  // State management
  const [privateSessions, setPrivateSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [selectedSessionForPlayers, setSelectedSessionForPlayers] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [sessionForCancelDelete, setSessionForCancelDelete] = useState(null);

  // Load private sessions
  const loadPrivateSessions = useCallback(async () => {
    try {
      setLoading(true);
      const sessions = await getPrivateSessions(currentUser.id);
      setPrivateSessions(sessions);
    } catch (err) {
      console.error('Error loading private sessions:', err);
      setError('Failed to load private sessions');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  // Load sessions on mount
  useEffect(() => {
    if (currentUser?.id) {
      loadPrivateSessions();
    }
  }, [currentUser?.id, loadPrivateSessions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Handle session creation
  const handleSessionCreated = useCallback(() => {
    setShowCreateModal(false);
    loadPrivateSessions();
  }, [loadPrivateSessions]);

  // Handle session join
  const handleSessionJoined = useCallback(() => {
    setShowJoinModal(false);
    loadPrivateSessions();
  }, [loadPrivateSessions]);

  // Handle add player modal
  const handleAddPlayerModalClose = useCallback(() => {
    setShowAddPlayerModal(false);
    setSelectedSessionForPlayers(null);
  }, []);

  const handlePlayerAdded = useCallback(() => {
    setShowAddPlayerModal(false);
    setSelectedSessionForPlayers(null);
    loadPrivateSessions(); // Refresh to show updated player count
  }, [loadPrivateSessions]);

  // Handle session actions
  const handleSessionAction = useCallback(async (sessionId, action) => {
    try {
      switch (action) {
        case 'activate':
          await updateSessionStatus(sessionId, 'active');
          break;
        case 'edit':
          // Navigate to session to edit
          navigate(`/session/${sessionId}`);
          return;
        case 'makeLive':
          await updateSessionStatus(sessionId, 'ongoing');
          break;
        case 'pause':
          await updateSessionStatus(sessionId, 'paused');
          break;
        case 'complete':
          await updateSessionStatus(sessionId, 'completed');
          break;
        case 'cancel':
          await updateSessionStatus(sessionId, 'cancelled');
          break;
        case 'addPlayers':
          // Open add player modal for this session
          setSelectedSessionForPlayers(sessionId);
          setShowAddPlayerModal(true);
          setActiveDropdown(null);
          return;
        case 'manage':
          // Navigate to session management
          navigate(`/session/${sessionId}`);
          return;
        default:
          console.warn('Unknown action:', action);
      }
      
      // Close dropdown and reload sessions
      setActiveDropdown(null);
      loadPrivateSessions();
    } catch (err) {
      console.error('Error performing session action:', err);
      setError(`Failed to ${action} session`);
    }
  }, [loadPrivateSessions, navigate]);

  // Toggle dropdown
  const toggleDropdown = useCallback((sessionId) => {
    setActiveDropdown(activeDropdown === sessionId ? null : sessionId);
  }, [activeDropdown]);

  // Bucket sessions into Future, Ongoing, Completed, Cancelled (order for display)
  const sessionsBySection = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const future = [];
    const ongoing = [];
    const completed = [];
    const cancelled = [];
    for (const s of privateSessions) {
      if (s.session_status === 'ongoing') ongoing.push(s);
      else if (s.session_status === 'completed') completed.push(s);
      else if (s.session_status === 'cancelled') cancelled.push(s);
      else if (s.session_status === 'draft' || s.session_status === 'active') future.push(s);
    }
    return { future, ongoing, completed, cancelled };
  }, [privateSessions]);

  const handleCancelOrDeleteSession = useCallback(async (sessionId, action) => {
    try {
      if (action === 'cancel') {
        await updateSessionStatus(sessionId, 'cancelled');
      } else {
        await deleteSession(sessionId);
      }
      setSessionForCancelDelete(null);
      setActiveDropdown(null);
      loadPrivateSessions();
      setError(null);
    } catch (err) {
      console.error(`Error ${action === 'cancel' ? 'cancelling' : 'deleting'} session:`, err);
      setError(err.message || `Failed to ${action === 'cancel' ? 'cancel' : 'delete'} session`);
    }
  }, [loadPrivateSessions]);

  // Get session status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'ongoing':
        return 'green';
      case 'draft':
        return 'yellow';
      case 'completed':
        return 'blue';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Format session date and time
  const formatSessionDateTime = (session) => {
    const date = new Date(session.session_date);
    const time = session.start_time;
    return `${date.toLocaleDateString()} at ${time}`;
  };

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const isSessionDatePassed = (session) => session.session_date < today;

  const renderSessionCard = (session) => (
    <Card key={session.id} variant="default" size="medium" className="overflow-visible">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <Typography variant="h3" className="text-lg font-semibold mb-1">
              {session.name}
            </Typography>
            <Typography variant="body" className="text-text-secondary mb-2">
              {formatSessionDateTime(session)}
            </Typography>
            <Typography variant="caption" className="text-text-muted">
              Host: {session.host_player?.first_name} {session.host_player?.last_name}
            </Typography>
          </div>
          <StatusBadge
            variant={getStatusColor(session.session_status)}
            size="small"
          >
            {session.session_status}
          </StatusBadge>
        </div>

        {session.location && (
          <div className="mb-4">
            <Typography variant="caption" className="text-text-muted">
              📍 {session.location}
            </Typography>
          </div>
        )}

        {session.description && (
          <div className="mb-4">
            <Typography variant="body" className="text-text-secondary">
              {session.description}
            </Typography>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={() => navigate(`/session/${session.id}`)}
            variant="primary"
            size="small"
            className="flex-1"
          >
            {session.session_status === 'active' || session.session_status === 'ongoing' ? 'Enter Session' : 'View Session'}
          </Button>

          {/* Actions Dropdown - draft, active, ongoing (full actions); cancelled (delete only) */}
          {(session.session_status === 'draft' || session.session_status === 'active' || session.session_status === 'ongoing' || session.session_status === 'cancelled') && (
          <div className="relative dropdown-container">
            <Button
              onClick={() => toggleDropdown(session.id)}
              variant="outline"
              size="small"
              className="px-3"
            >
              Actions ▼
            </Button>

            {activeDropdown === session.id && (
              <div className="absolute right-0 mt-1 w-48 bg-surface rounded-md shadow-lg z-50 border border-border-medium">
                <div className="py-1">
                  {session.session_status === 'draft' && (
                    <>
                      <button
                        onClick={() => handleSessionAction(session.id, 'edit')}
                        className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-subtle"
                      >
                        ✏️ Edit Session
                      </button>
                      <button
                        onClick={() => handleSessionAction(session.id, 'makeLive')}
                        className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-subtle"
                      >
                        ✅ Make Live
                      </button>
                    </>
                  )}

                  {session.session_status === 'active' && (
                    <>
                      <button
                        onClick={() => handleSessionAction(session.id, 'makeLive')}
                        className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-subtle"
                      >
                        ✅ Make Live
                      </button>
                      <button
                        onClick={() => handleSessionAction(session.id, 'addPlayers')}
                        className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-subtle"
                      >
                        👥 View Signups
                      </button>
                    </>
                  )}

                  {session.session_status === 'ongoing' && (
                    <>
                      {isSessionDatePassed(session) && (
                        <button
                          onClick={() => handleSessionAction(session.id, 'complete')}
                          className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-subtle"
                        >
                          ✔️ End Session
                        </button>
                      )}
                      <button
                        onClick={() => handleSessionAction(session.id, 'addPlayers')}
                        className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-surface-subtle"
                      >
                        👥 View Signups
                      </button>
                    </>
                  )}

                  {(session.session_status === 'draft' || session.session_status === 'active' || session.session_status === 'ongoing' || session.session_status === 'cancelled') && (
                    <>
                      {(session.session_status === 'draft' || session.session_status === 'active' || session.session_status === 'ongoing') && <hr className="my-1 border-border-medium" />}
                      <button
                        onClick={() => {
                          setSessionForCancelDelete(session);
                          setActiveDropdown(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-danger-dark hover:bg-danger-light"
                      >
                        ❌ Cancel/Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <Typography variant="body">Loading private sessions...</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-app-shell mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-visible">
        {/* Header */}
        <div className="mb-8">
          <BackButton onClick={() => navigate('/')}>
            Back to Home
          </BackButton>
          <div className="mt-4">
            <Typography variant="h1" className="text-3xl font-bold text-text-primary mb-2">
              🎯 Private Sessions
            </Typography>
            <Typography variant="body" className="text-text-secondary">
              Create and manage your private badminton sessions with friends
            </Typography>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <Card variant="default" size="large">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Typography variant="h3" className="text-lg font-semibold mb-2">
                  Quick Actions
                </Typography>
                <Typography variant="body" className="text-text-secondary mb-4">
                  Start a new private session or join an existing one
                </Typography>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => setShowJoinModal(true)}
                  variant="outline"
                  size="medium"
                >
                  Join by Code
                </Button>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  variant="primary"
                  size="medium"
                >
                  Create Session
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Sessions List by Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <Typography variant="h2" className="text-xl font-semibold">
              Your Private Sessions ({privateSessions.length})
            </Typography>
            <Button
              onClick={loadPrivateSessions}
              variant="outline"
              size="small"
            >
              Refresh
            </Button>
          </div>

          {privateSessions.length > 0 ? (
            <div className="space-y-10">
              {sessionsBySection.future.length > 0 && (
                <div>
                  <Typography variant="h3" className="text-lg font-semibold text-text-primary mb-3">
                    Future ({sessionsBySection.future.length})
                  </Typography>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 overflow-visible">
                    {sessionsBySection.future.map(renderSessionCard)}
                  </div>
                </div>
              )}
              {sessionsBySection.ongoing.length > 0 && (
                <div>
                  <Typography variant="h3" className="text-lg font-semibold text-text-primary mb-3">
                    Ongoing ({sessionsBySection.ongoing.length})
                  </Typography>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 overflow-visible">
                    {sessionsBySection.ongoing.map(renderSessionCard)}
                  </div>
                </div>
              )}
              {sessionsBySection.completed.length > 0 && (
                <div>
                  <Typography variant="h3" className="text-lg font-semibold text-text-primary mb-3">
                    Completed ({sessionsBySection.completed.length})
                  </Typography>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 overflow-visible">
                    {sessionsBySection.completed.map(renderSessionCard)}
                  </div>
                </div>
              )}
              {sessionsBySection.cancelled.length > 0 && (
                <div>
                  <Typography variant="h3" className="text-lg font-semibold text-text-primary mb-3">
                    Cancelled ({sessionsBySection.cancelled.length})
                  </Typography>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 overflow-visible">
                    {sessionsBySection.cancelled.map(renderSessionCard)}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Card variant="default" size="large">
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎯</div>
                <Typography variant="h3" className="text-xl font-semibold mb-2">
                  No private sessions yet
                </Typography>
                <Typography variant="body" className="text-text-secondary mb-6">
                  Create your first private session or ask a friend for a join code
                </Typography>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => setShowJoinModal(true)}
                    variant="outline"
                    size="medium"
                  >
                    Join by Code
                  </Button>
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    variant="primary"
                    size="medium"
                  >
                    Create Session
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-danger-light border border-danger text-danger-dark px-4 py-3 rounded mb-4">
            {error}
            <button 
              onClick={() => setError(null)}
              className="ml-2 text-danger-dark hover:text-danger"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreatePrivateSessionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSessionCreated={handleSessionCreated}
          currentUser={currentUser}
        />
      )}

      {showJoinModal && (
        <JoinSessionModal
          isOpen={showJoinModal}
          onClose={() => setShowJoinModal(false)}
          onSessionJoined={handleSessionJoined}
          currentUser={currentUser}
        />
      )}

      {showAddPlayerModal && selectedSessionForPlayers && (
        <AddPlayerModal
          isOpen={showAddPlayerModal}
          onClose={handleAddPlayerModalClose}
          onPlayerAdded={handlePlayerAdded}
          sessionId={selectedSessionForPlayers}
          currentUser={currentUser}
        />
      )}

      <CancelDeleteModal
        isOpen={!!sessionForCancelDelete}
        onClose={() => setSessionForCancelDelete(null)}
        onCancel={() => sessionForCancelDelete && handleCancelOrDeleteSession(sessionForCancelDelete.id, 'cancel')}
        onDelete={() => sessionForCancelDelete && handleCancelOrDeleteSession(sessionForCancelDelete.id, 'delete')}
        sessionName={sessionForCancelDelete?.name || 'This session'}
      />
    </div>
  );
};

export default PrivateSessionsPage;
